import { Component, ElementRef, HostListener, Injector, Input, Output, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AppDialogComponentBase } from '@shared/app-dialog-component-base';
import { CompanyDto, CompanyServiceProxy, JuridicalPersonServiceProxy, NaturalPersonDto, NaturalPersonServiceProxy, ReminderDto, ReminderPriorityDto, ReminderRecipientDto, ReminderServiceProxy, ReminderTypeDto, UserDto, UserServiceProxy } from '@shared/service-proxies/service-proxies';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { AppConsts } from '@shared/AppConsts';
import * as moment from 'moment';
import { DatepickerDateCustomClasses } from '@node_modules/ngx-bootstrap/datepicker';
import { DateHelper } from '@shared/helpers/DateHelper';
import { PermissionCheckerService } from '@node_modules/abp-ng2-module';
import { ReminderService } from '@app/reminders/reminder.service';

@Component({
  selector: 'app-create-or-edit-reminder',
  templateUrl: './create-or-edit-reminder.component.html',
  styleUrl: './create-or-edit-reminder.component.css'
})
export class CreateOrEditReminderComponent extends AppDialogComponentBase {

  @Input() reminderId?: number;
  @Input() eventData?: {
    companyId?: number;
    juridicalPersonId?: number;
    naturalPersonId?: number;
    date?: moment.Moment;
  };
  @Input() prefilledData?: {
    description?: string;
    assignedRecipients?: number[];
    reminderDate?: moment.Moment;
  };

  @Input() onSaveCallback?: (savedReminder: any) => void;
  @Input() onDeleteCallback?: () => void;

  @ViewChild('comboWrapper') comboWrapper: ElementRef;
  @ViewChild('assignedFieldMenu') assignedFieldMenu: ContextMenu;
  @ViewChild('priorityListContainer') priorityListContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('typeListContainer') typeListContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('startInput', { static: false }) startInput: ElementRef;
  @ViewChild('endInput', { static: false }) endInput: ElementRef;
  @ViewChild('timeEl', { static: false }) timeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('createOrEditReminderForm', { static: false }) createOrEditReminderForm!: any;

  saving: boolean = false;

  reminder = new ReminderDto();
  reminderPriorities: ReminderPriorityDto[] = [];
  reminderTypes: ReminderTypeDto[] = [];

  originalReminderPriorities: ReminderPriorityDto[] = [];
  originalReminderTypes: ReminderTypeDto[] = [];

  showPriorityDropdown = false;
  showTypeDropdown = false;
  isEditingTypeOptions = false;
  isEditingPriorityOptions = false;
  repeat = false;
  newPriorityName = '';
  newTypeName = '';
  originalReminderJson = '';
  dateString = '';
  endDateString = '';
  timeString = '';
  assignedFieldSearchField = '';
  recipientOptions: UserDto[] = [];
  assignedRecipientsIds: number[] = [];
  assignedFieldMenuItems: MenuItem[] = [];

  timezoneList: string[] = Array.from({ length: 25 }, (_, i) => {
    const offset = i - 12;
    const sign = offset >= 0 ? '+' : '';
    return `UTC${sign}${offset.toString().padStart(2, '0')}:00`;
  });
  selectedTimezone = 'UTC-3:00';

  selectedNotifyBefore = '';
  notifyBeforeList: string[] = [this.l('SameDay'), this.l('1Day'), this.l('2Days')];

  customDateClasses: DatepickerDateCustomClasses[] = [];

  repeatModes: any[] = [{ label: this.l('Daily'), value: 'Daily' }, { label: this.l('Weekly'), value: 'Weekly' }, { label: this.l('Monthly'), value: 'Monthly' }];
  repeatModeSelected = "Daily";

  canEditRemindersTypes = false;
  canEditRemindersPriorities = false;

  weekdays = [
    { name: this.l('Monday'), value: 1 },
    { name: this.l('Tuesday'), value: 2 },
    { name: this.l('Wednesday'), value: 3 },
    { name: this.l('Thursday'), value: 4 },
    { name: this.l('Friday'), value: 5 },
    { name: this.l('Saturday'), value: 6 },
    { name: this.l('Sunday'), value: 7 },
  ];

  monthOrdinals = [
    { label: this.l('FirstOrdinal'), value: 1 },
    { label: this.l('SecondOrdinal'), value: 2 },
    { label: this.l('ThirdOrdinal'), value: 3 },
    { label: this.l('FourthOrdinal'), value: 4 },
    { label: this.l('Last'), value: 0 },
  ];

  monthlyOrdinalSelected: number | null = null;

  weekdaysSelectOptions = this.weekdays.map(d => ({
    label: d.name,
    value: d.value
  }));

  fromDateSelected = "";
  toDateSelected = "";
  weeklyDayInput = "";
  monthlyWeekdayInput = "";
  monthlyOrdinalInput = "";

  rangeStart = 0;
  rangeEnd = 0;

  randomTypeId!: string;
  randomPriorityId!: string;

  quillModules = {
    toolbar: [
      [{ 'font': [] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
    ]
  };

  minDate!: Date;
  isDateTimeValid: boolean = true;

  haveChangesToSave: boolean = false;

  constructor(
    injector: Injector,
    private _reminderService: ReminderServiceProxy,
    private _companyService: CompanyServiceProxy,
    private _juridicalPersonService: JuridicalPersonServiceProxy,
    private _naturalPersonService: NaturalPersonServiceProxy,
    private _userService: UserServiceProxy,
    public bsModalRef: BsModalRef,
    private permissionChecker: PermissionCheckerService,
    public _reminderServiceParam: ReminderService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.randomTypeId = 'dropdown-' + Math.random().toString(36).substr(2, 9);
    this.randomPriorityId = 'dropdown-' + Math.random().toString(36).substr(2, 9);
    this.minDate = new Date();

    this.canEditRemindersTypes = this.permissionChecker.isGranted('TypesReminders');
    this.canEditRemindersPriorities = this.permissionChecker.isGranted('PrioritiesReminders');

    this._companyService.getAll(undefined, true, undefined, '', 0, undefined, undefined, 0, AppConsts.maxResultsCount).subscribe(result => {
      this.assignedFieldMenuItems.push({
        label: this.l("Society"),
        items: result.items.map(p => { return { label: p.name + ' ' + p.companyTypeCode, command: () => this.selectAssignedField(p, undefined, undefined) }; }),
      });

      if (this.router.url.match("app/companies/view/")) {
        const parts = this.router.url.split("/");
        const companyId = Number.parseInt(parts[parts.length - 1]);
        this.reminder.companyId = companyId;
        this.assignedFieldSearchField = result.items.find(f => f.id == companyId).name + ' ' + result.items.find(f => f.id == companyId).companyTypeCode;
      }

      this._juridicalPersonService.getAll(undefined, undefined, undefined, '', 0, undefined, undefined, 0, AppConsts.maxResultsCount).subscribe(result => {
        this.assignedFieldMenuItems.push({
          label: this.l("JuridicalPerson"),
          items: result.items.map(p => { return { label: p.name + ' ' + p.companyTypeCode, command: () => this.selectAssignedField(undefined, p, undefined) }; })
        });

        if (this.router.url.match("persons/juridical/view/")) {
          const parts = this.router.url.split("/");
          const juridicalId = Number.parseInt(parts[parts.length - 1]);
          this.reminder.juridicalPersonId = juridicalId;
          this.assignedFieldSearchField = result.items.find(f => f.id == juridicalId).name + ' ' + result.items.find(f => f.id == juridicalId).companyTypeCode;;
        }

        this._naturalPersonService.getAll(undefined, undefined, undefined, '', 0, undefined, undefined, 0, AppConsts.maxResultsCount).subscribe(result => {
          this.assignedFieldMenuItems.push({
            label: this.l("NaturalPerson"),
            items: result.items.map(p => { return { label: p.names + " " + p.surnames, command: () => this.selectAssignedField(undefined, undefined, p) }; })
          });

          if (this.router.url.match("persons/natural/view/")) {
            const parts = this.router.url.split("/");
            const naturalPersonId = Number.parseInt(parts[parts.length - 1]);
            this.reminder.naturalPersonId = naturalPersonId;
            const naturalPerson = result.items.find(f => f.id == naturalPersonId);
            this.assignedFieldSearchField = naturalPerson.names + " " + naturalPerson.surnames;
          }

          this.assignedFieldMenuItems.push({
            label: this.l("Unassigned"),
            command: () => this.clearAssignedField()
          });
        });
      });
    });

    this._userService
      .getAll(true, '', 0, undefined, undefined, 0, AppConsts.maxResultsCount)
      .subscribe(result => {
        const currentUserId = this.appSession.userId;

        this.recipientOptions = result.items.sort((a, b) => {
          if (a.id === currentUserId) return -1;
          if (b.id === currentUserId) return 1;

          const nameA = (a.fullName ?? '').toLowerCase();
          const nameB = (b.fullName ?? '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });


    this._reminderService.getAllReminderPriorities().subscribe(result => {
      this.reminderPriorities = result.map(p => {
        (p as any).uid = (p as any).uid || this.generateUid();
        return p;
      });
      this.checkPrioritiesUsage();
    });

    this._reminderService.getAllReminderTypes().subscribe(result => {
      this.reminderTypes = result.map(t => {
        (t as any).uid = (t as any).uid || this.generateUid();
        return t;
      });
      this.checkTypesUsage();
    });

    if (this.reminderId) {
      this._reminderService.get(this.reminderId).subscribe(result => {
        this.reminder = result;
        this.originalReminderJson = JSON.stringify(this.reminder);
        this.title = this.l('EditReminder');

        if (this.reminder.companyId != undefined)
          this.assignedFieldSearchField = this.reminder.company.name;

        if (this.reminder.juridicalPersonId != undefined)
          this.assignedFieldSearchField = this.reminder.juridicalPerson.name;

        if (this.reminder.naturalPersonId != undefined)
          this.assignedFieldSearchField = this.reminder.naturalPerson.names + " " + this.reminder.naturalPerson.surnames;

        this.assignedRecipientsIds = this.reminder.reminderRecipients.map(m => m.user.id);

        this.dateString = DateHelper.formatDate(this.reminder.executionDates[0], this.getDateInputFormat());

        if (this.reminder.endDate)
          this.endDateString = DateHelper.formatDate(this.reminder.endDate, this.getDateInputFormat());

        if (this.reminder.dateToSelected) {
          switch (this.reminder.dateToSelected) {
            case 'Monday':
              this.toDateSelected = this.l('Monday');
              break;
            case 'Tuesday':
              this.toDateSelected = this.l('Tuesday');
              break;
            case 'Wednesday':
              this.toDateSelected = this.l('Wednesday');
              break;
            case 'Thursday':
              this.toDateSelected = this.l('Thursday');
              break;
            case 'Friday':
              this.toDateSelected = this.l('Friday');
              break;
            case 'Saturday':
              this.toDateSelected = this.l('Saturday');
              break;
            case 'Sunday':
              this.toDateSelected = this.l('Sunday');
              break;
            default:
              break;
          }
        }

        if (this.reminder.dateFromSelected) {
          switch (this.reminder.dateFromSelected) {
            case 'Monday':
              this.fromDateSelected = this.l('Monday');
              break;
            case 'Tuesday':
              this.fromDateSelected = this.l('Tuesday');
              break;
            case 'Wednesday':
              this.fromDateSelected = this.l('Wednesday');
              break;
            case 'Thursday':
              this.fromDateSelected = this.l('Thursday');
              break;
            case 'Friday':
              this.fromDateSelected = this.l('Friday');
              break;
            case 'Saturday':
              this.fromDateSelected = this.l('Saturday');
              break;
            case 'Sunday':
              this.fromDateSelected = this.l('Sunday');
              break;
            default:
              break;
          }
        }

        if (this.reminder.monthlyOrdinalSelected) {
          this.repeatModeSelected = 'Monthly';
          switch (this.reminder.monthlyOrdinalSelected) {
            case 'FirstOrdinal':
              this.monthlyOrdinalInput = this.l('FirstOrdinal');
              break;
            case 'SecondOrdinal':
              this.monthlyOrdinalInput = this.l('SecondOrdinal');
              break;
            case 'ThirdOrdinal':
              this.monthlyOrdinalInput = this.l('ThirdOrdinal');
              break;
            case 'FourthOrdinal':
              this.monthlyOrdinalInput = this.l('FourthOrdinal');
              break;
            case 'Last':
              this.monthlyOrdinalInput = this.l('Last');
              break;
            default:
              break;
          }
        }

        if (this.reminder.executionDates.length > 1) {
          this.repeat = true;
          this.customDateClasses = this.reminder.executionDates.map(d => ({
            date: d.toDate(),
            classes: ['selected-date']
          }));
        }

        this.selectedTimezone = this.reminder.utc;

        if (this.reminder.daysBefore == 1)
          this.selectedNotifyBefore = this.l("1Day");

        if (this.reminder.daysBefore == 2)
          this.selectedNotifyBefore = this.l("2Days");

        this.timeString = this.reminder.time.utc(false).format('HH:mm');
      });
    }
    else {
      this.originalReminderJson = JSON.stringify(this.reminder);

      if (this.eventData) {
        this.handleEventData();
      }

      if (this.prefilledData) {
        this.handlePrefilledData();
      }
    }

    setTimeout(() => {
      if (!document.querySelector('.modal-backdrop')) {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
        document.body.classList.add('modal-open');
      }
    }, 50);
  }

  clearAssignedField() {
    this.assignedFieldSearchField = "";
    this.reminder.companyId = undefined;
    this.reminder.juridicalPersonId = undefined;
    this.reminder.naturalPersonId = undefined;
  }

  save() {
    if (!this.timezoneList.some(f => f == this.selectedTimezone)) {
      this.notify.success(this.l('TimeZoneSelectedNotCorrect'));
      return;
    }

    if (this.selectedNotifyBefore != '' && !this.notifyBeforeList.some(f => f == this.selectedNotifyBefore)) {
      this.notify.success(this.l('NotifyBeforeSelectedNotCorrect'));
      return;
    }

    this.reminder.reminderRecipients = [];
    this.reminder.time = moment(this.timeString, "HH:mm").utc(true);
    this.reminder.utc = this.selectedTimezone;

    if (this.repeat) {
      this.reminder.daysBefore = undefined;
    } else {
      if (this.selectedNotifyBefore == this.l("1Day"))
        this.reminder.daysBefore = 1;

      if (this.selectedNotifyBefore == this.l("2Days"))
        this.reminder.daysBefore = 2;

      if (this.selectedNotifyBefore != this.l("2Days") && this.selectedNotifyBefore != this.l("1Day"))
        this.reminder.daysBefore = undefined;
    }

    if (!this.repeat)
      this.reminder.executionDates = [moment(this.dateString, this.getDateInputFormat()).utc(false)];

    this.reminder.endDate = moment(this.endDateString, this.getDateInputFormat()).utc(false);

    this.assignedRecipientsIds.forEach(element => {
      let reminderReciepient = new ReminderRecipientDto();
      reminderReciepient.userId = element;
      this.reminder.reminderRecipients.push(reminderReciepient);
    });

    if (this.toDateSelected) {
      switch (this.toDateSelected) {
        case this.l('Monday'):
          this.reminder.dateToSelected = 'Monday';
          break;
        case this.l('Tuesday'):
          this.reminder.dateToSelected = 'Tuesday';
          break;
        case this.l('Wednesday'):
          this.reminder.dateToSelected = 'Wednesday';
          break;
        case this.l('Thursday'):
          this.reminder.dateToSelected = 'Thursday';
          break;
        case this.l('Friday'):
          this.reminder.dateToSelected = 'Friday';
          break;
        case this.l('Saturday'):
          this.reminder.dateToSelected = 'Saturday';
          break;
        case this.l('Sunday'):
          this.reminder.dateToSelected = 'Sunday';
          break;
        default:
          this.reminder.dateToSelected = '';
          break;
      }
    }

    if (this.fromDateSelected) {
      switch (this.fromDateSelected) {
        case this.l('Monday'):
          this.reminder.dateFromSelected = 'Monday';
          break;
        case this.l('Tuesday'):
          this.reminder.dateFromSelected = 'Tuesday';
          break;
        case this.l('Wednesday'):
          this.reminder.dateFromSelected = 'Wednesday';
          break;
        case this.l('Thursday'):
          this.reminder.dateFromSelected = 'Thursday';
          break;
        case this.l('Friday'):
          this.reminder.dateFromSelected = 'Friday';
          break;
        case this.l('Saturday'):
          this.reminder.dateFromSelected = 'Saturday';
          break;
        case this.l('Sunday'):
          this.reminder.dateFromSelected = 'Sunday';
          break;
        default:
          this.reminder.dateFromSelected = '';
          break;
      }
    }

    if (this.monthlyOrdinalInput) {
      switch (this.monthlyOrdinalInput) {
        case this.l('FirstOrdinal'):
          this.reminder.monthlyOrdinalSelected = 'FirstOrdinal';
          break;
        case this.l('SecondOrdinal'):
          this.reminder.monthlyOrdinalSelected = 'SecondOrdinal';
          break;
        case this.l('ThirdOrdinal'):
          this.reminder.monthlyOrdinalSelected = 'ThirdOrdinal';
          break;
        case this.l('FourthOrdinal'):
          this.reminder.monthlyOrdinalSelected = 'FourthOrdinal';
          break;
        case this.l('Last'):
          this.reminder.monthlyOrdinalSelected = 'Last';
          break;
        default:
          this.reminder.monthlyOrdinalSelected = '';
          break;
      }
    }

    if (this.reminder.id > 0) {
      this._reminderService.update(this.reminder).subscribe(result => {
        this.notify.success(this.l('SavedSuccessfully'));
        if (this.onSaveCallback) {
          this.onSaveCallback(result);
        }
        this.onCloseModal();
        this._reminderServiceParam.triggerRefresh();
      });
    } else {
      this._reminderService.create(this.reminder).subscribe(result => {
        this.notify.success(this.l('SavedSuccessfully'));
        if (this.onSaveCallback) {
          this.onSaveCallback(result);
        }
        this.onCloseModal();
        this._reminderServiceParam.triggerRefresh();
      });
    }
  }

  preventEnter(event: KeyboardEvent) {
    event.preventDefault();
  }

  toggleTypeEditMode(): void {
    if (this.isEditingPriorityOptions) {
      this.cancelEditPriorities();
    }
    this.isEditingTypeOptions = !this.isEditingTypeOptions;
    if (this.isEditingTypeOptions) {
      this.originalReminderTypes = this.reminderTypes.map(t => Object.assign(new ReminderTypeDto(), t));
      setTimeout(() => {
        const container = this.typeListContainer?.nativeElement;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 5);
    } else {
      this.showTypeDropdown = false;
    }
  }

  cancelEditPriorities() {
    this.reminderPriorities = this.originalReminderPriorities.map(p => Object.assign(new ReminderPriorityDto(), p));
    this.newPriorityName = '';
    this.isEditingPriorityOptions = false;
    this.showPriorityDropdown = false;
  }

  getTypeName(id: number | null): string {
    if (id == null) return '';
    const found = this.reminderTypes.find(t => t.id === id);
    return found ? found.name : '';
  }

  clearType(): void {
    this.reminder.reminderTypeId = null;
    this.showTypeDropdown = false;
  }

  toggleTypeDropdown(): void {
    this.showTypeDropdown = !this.showTypeDropdown;
    if (this.showTypeDropdown) {
      this.showPriorityDropdown = false;
    }
  }

  selectType(typeId: number | null): void {
    this.reminder.reminderTypeId = typeId;
    this.showTypeDropdown = false;
    this.haveChangesToSave = true;
  }

  onDropType(event: CdkDragDrop<ReminderTypeDto[]>) {
    const clonedArray = [...this.reminderTypes];
    moveItemInArray(clonedArray, event.previousIndex, event.currentIndex);
    this.reminderTypes = [...clonedArray];
  }

  trackByType(index: number, item: any): string | number {
    return item.uid || item.id || index;
  }

  isTypeInUse(type: ReminderTypeDto): boolean {
    return (type as any).isUsed === true;
  }

  checkTypesUsage(): void {
    this.reminderTypes.forEach(type => {
      if (type.id && type.id !== 0) {
        this._reminderService.isReminderTypeInUse(type.id).subscribe(isUsed => {
          (type as any).isUsed = isUsed;
        });
      }
    });
  }

  deleteType(index: number, event?: MouseEvent) {
    const id = this.reminderTypes[index].id;
    if (id && id !== 0) {
      this._reminderService.isReminderTypeInUse(id).subscribe(isUsed => {
        if (isUsed) {
          this.message.warn(this.l('CannotDeleteTypeReminderInUse'));
          if (event) {
            event.stopPropagation();
          }
          return;
        }
        this.reminderTypes.splice(index, 1);
      });
    } else {
      this.reminderTypes.splice(index, 1);
    }
    if (event) {
      event.stopPropagation();
    }
  }

  onEnterAddType(event: KeyboardEvent) {
    event.preventDefault();
    this.addType();
  }

  addType() {
    const name = this.newTypeName?.trim();
    if (!name) return;
    const normalizedName = this.normalizeText(name);
    const alreadyExists = this.reminderTypes.some(t => this.normalizeText(t.name) === normalizedName);
    if (alreadyExists) {
      this.message.warn(this.l('TypeAlreadyExists'));
      return;
    }
    const newItem = new ReminderTypeDto();
    newItem.id = 0;
    newItem.name = name;
    (newItem as any).uid = this.generateUid();
    this.reminderTypes = [...this.reminderTypes, newItem];
    this.newTypeName = '';
    setTimeout(() => {
      if (this.typeListContainer) {
        const el = this.typeListContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }

  generateUid(): string {
    return '_' + Math.random().toString(36).substr(2, 9);
  }

  normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');
  }

  cancelEditTypes() {
    this.reminderTypes = this.originalReminderTypes.map(t => Object.assign(new ReminderTypeDto(), t));
    this.newTypeName = '';
    this.isEditingTypeOptions = false;
    this.showTypeDropdown = false;
  }

  saveTypes() {
    const selectedId = this.reminder.reminderTypeId;
    this.reminderTypes.forEach((t, index) => {
      t.order = index;
    });
    this._reminderService.syncReminderTypes(this.reminderTypes).subscribe(() => {
      this._reminderService.getAllReminderTypes().subscribe(result => {
        this.reminderTypes = result.map(t => {
          (t as any).uid = (t as any).uid || this.generateUid();
          return t;
        });
        this.checkTypesUsage();
        this.isEditingTypeOptions = false;

        if (!this.reminderTypes.some(t => t.id === selectedId)) {
          this.reminder.reminderTypeId = null;
        } else {
          this.reminder.reminderTypeId = selectedId;
        }
      });
    });
  }

  togglePriorityEditMode(): void {
    if (this.isEditingTypeOptions) {
      this.cancelEditTypes();
    }
    this.isEditingPriorityOptions = !this.isEditingPriorityOptions;
    if (this.isEditingPriorityOptions) {
      this.originalReminderPriorities = this.reminderPriorities.map(p => Object.assign(new ReminderPriorityDto(), p));
      setTimeout(() => {
        const container = this.priorityListContainer?.nativeElement;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 5);
    } else {
      this.showPriorityDropdown = false;
    }
  }

  getPriorityName(id: number | null): string {
    if (id == null) return '';
    const found = this.reminderPriorities.find(p => p.id === id);
    return found ? found.name : '';
  }

  togglePriorityDropdown(): void {
    this.showPriorityDropdown = !this.showPriorityDropdown;
    if (this.showPriorityDropdown) {
      this.showTypeDropdown = false;
    }
  }

  clearPriority(): void {
    this.reminder.reminderPriorityId = null;
    this.showPriorityDropdown = false;
  }

  selectPriority(priorityId: number | null): void {
    this.reminder.reminderPriorityId = priorityId;
    this.showPriorityDropdown = false;
    this.haveChangesToSave = true;
  }

  onDropPriority(event: CdkDragDrop<ReminderPriorityDto[]>) {
    const clonedArray = [...this.reminderPriorities];
    moveItemInArray(clonedArray, event.previousIndex, event.currentIndex);
    this.reminderPriorities = [...clonedArray];
  }

  trackByPriority(index: number, item: any): string | number {
    return item.uid || item.id || index;
  }

  isPriorityInUse(priority: ReminderPriorityDto): boolean {
    return (priority as any).isUsed === true;
  }

  checkPrioritiesUsage(): void {
    this.reminderPriorities.forEach(priority => {
      if (priority.id && priority.id !== 0) {
        this._reminderService.isReminderPriorityInUse(priority.id).subscribe(isUsed => {
          (priority as any).isUsed = isUsed;
        });
      }
    });
  }

  deletePriority(index: number, event?: MouseEvent) {
    const id = this.reminderPriorities[index].id;
    if (id && id !== 0) {
      this._reminderService.isReminderPriorityInUse(id).subscribe(isUsed => {
        if (isUsed) {
          this.message.warn(this.l('CannotDeletePriorityInUse'));
          if (event) {
            event.stopPropagation();
          }
          return;
        }
        this.reminderPriorities.splice(index, 1);
      });
    } else {
      this.reminderPriorities.splice(index, 1);
    }
    if (event) {
      event.stopPropagation();
    }
  }

  onEnterAddPriority(event: KeyboardEvent) {
    event.preventDefault();
    this.addPriority();
  }

  addPriority() {
    const name = this.newPriorityName?.trim();
    if (!name) return;
    const normalizedName = this.normalizeText(name);
    const alreadyExists = this.reminderPriorities.some(p => this.normalizeText(p.name) === normalizedName);
    if (alreadyExists) {
      this.message.warn(this.l('PriorityAlreadyExists'));
      return;
    }
    const newItem = new ReminderPriorityDto();
    newItem.id = 0;
    newItem.name = name;
    (newItem as any).uid = this.generateUid();
    this.reminderPriorities = [...this.reminderPriorities, newItem];
    this.newPriorityName = '';
    setTimeout(() => {
      if (this.priorityListContainer) {
        const el = this.priorityListContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
    this.cd.detectChanges();
  }

  savePriorities() {
    const selectedId = this.reminder.reminderPriorityId;
    this.reminderPriorities.forEach((p, index) => {
      p.order = index;
    });
    this._reminderService.syncReminderPriorities(this.reminderPriorities).subscribe(() => {
      this._reminderService.getAllReminderPriorities().subscribe(result => {
        this.reminderPriorities = result.map(p => {
          (p as any).uid = (p as any).uid || this.generateUid();
          return p;
        });
        this.checkPrioritiesUsage();
        this.isEditingPriorityOptions = false;

        if (!this.reminderPriorities.some(p => p.id === selectedId)) {
          this.reminder.reminderPriorityId = null;
        } else {
          this.reminder.reminderPriorityId = selectedId;
        }
      });
    });
  }

  fixZIndex() {
    const menus = document.querySelectorAll('.p-tieredmenu') as NodeListOf<HTMLElement>;
    menus.forEach(menu => {
      menu.style.zIndex = '3000';
    });
  }

  openContextMenu(event: MouseEvent) {
    event.preventDefault();
    this.assignedFieldMenu.show(event);
    setTimeout(() => {
      this.repositionContextMenu();
    }, 0);
  }

  repositionContextMenu() {
    const menuEl = document.querySelector('.p-contextmenu.p-component') as HTMLElement;
    const triggerEl = this.comboWrapper.nativeElement as HTMLElement;
    if (menuEl && triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      const top = rect.bottom + window.scrollY;
      const left = rect.left + window.scrollX;
      menuEl.style.top = `${top}px`;
      menuEl.style.left = `${left}px`;
    }
  }

  selectAssignedField(company: CompanyDto, JuridicalPerson: CompanyDto, naturalPerson: NaturalPersonDto) {
    if (company != undefined) {
      this.assignedFieldSearchField = company.name + ' ' + company.companyTypeCode;
      this.reminder.companyId = company.id;
      this.reminder.juridicalPersonId = undefined;
      this.reminder.naturalPersonId = undefined;
    }
    if (JuridicalPerson != undefined) {
      this.assignedFieldSearchField = JuridicalPerson.name + ' ' + JuridicalPerson.companyTypeCode;
      this.reminder.juridicalPersonId = JuridicalPerson.id;
      this.reminder.companyId = undefined;
      this.reminder.naturalPersonId = undefined;
    }
    if (naturalPerson != undefined) {
      this.assignedFieldSearchField = naturalPerson.names + " " + naturalPerson.surnames;
      this.reminder.naturalPersonId = naturalPerson.id;
      this.reminder.companyId = undefined;
      this.reminder.juridicalPersonId = undefined;
    }
  }

  onTimezoneSelected(event: TypeaheadMatch) {
    this.selectedTimezone = event.item;
  }

  onNotifyBeforeSelected(event: TypeaheadMatch) {
    this.selectedNotifyBefore = event.item;
    this.validateDateTime();
  }

  onDateClick(date: Date) {
    const m = moment(date);
    if (!this.reminder.executionDates) {
      this.reminder.executionDates = [];
    }
    const idx = this.reminder.executionDates.findIndex(d => d.isSame(m, 'day'));
    if (idx > -1) {
      this.reminder.executionDates.splice(idx, 1);
    } else {
      this.reminder.executionDates.push(m);
    }
    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));
    this.validateDateTime();
  }

  removeExecutionDate(toRemove: moment.Moment) {
    this.reminder.executionDates = this.reminder.executionDates
      .filter(d => !d.isSame(toRemove, 'day'));
    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));
  }

  generateDates() {
    this.reminder.executionDates = [];

    const existing = this.reminder.executionDates!;
    const existingKeys = new Set(existing.map(d => d.format('YYYY-MM-DD')));

    const { from, to } = this.getRepeatDateRange();

    const today = from.startOf('day');
    const horizon = to.startOf('day');
    const newDates: moment.Moment[] = [];

    let cursor = today.clone();
    while (cursor.isSameOrBefore(horizon)) {
      const wd = cursor.isoWeekday();
      const inRange = this.rangeStart <= this.rangeEnd
        ? (wd >= this.rangeStart && wd <= this.rangeEnd)
        : (wd >= this.rangeStart || wd <= this.rangeEnd);

      if (inRange) {
        const key = cursor.format('YYYY-MM-DD');
        if (!existingKeys.has(key)) {
          newDates.push(cursor.clone());
          existingKeys.add(key);
        }
      }
      cursor.add(1, 'day');
    }

    this.reminder.executionDates = [
      ...existing,
      ...newDates
    ].sort((a, b) => a.valueOf() - b.valueOf());

    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));

    this.validateDateTime();
  }

  generateWeeklyDates() {
    this.reminder.executionDates = [];

    const existing = this.reminder.executionDates!;
    const existingKeys = new Set(existing.map(d => d.format('YYYY-MM-DD')));

    const date = this.dateString != undefined ? moment(this.dateString, this.getDateInputFormat()).startOf('day') : undefined;
    if (date == undefined) {
      console.warn('weeklyDayInput no coincide con ningún día');
      return;
    }
    const selectedWeekday = date.isoWeekday();

    const { from, to } = this.getRepeatDateRange();

    const today = from.startOf('day');
    const horizon = to.startOf('day');
    const newDates: moment.Moment[] = [];

    let cursor = today.clone();
    while (cursor.isSameOrBefore(horizon)) {
      if (cursor.isoWeekday() === selectedWeekday) {
        const key = cursor.format('YYYY-MM-DD');
        if (!existingKeys.has(key)) {
          newDates.push(cursor.clone());
          existingKeys.add(key);
        }
      }
      cursor.add(1, 'day');
    }

    this.reminder.executionDates = [
      ...existing,
      ...newDates
    ].sort((a, b) => a.valueOf() - b.valueOf());

    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));

    this.validateDateTime();
  }

  generateMonthlyDates() {
    this.reminder.executionDates = [];

    const existing = this.reminder.executionDates!;
    const existingKeys = new Set(existing.map(d => d.format('YYYY-MM-DD')));

    const date = this.dateString != undefined ? moment(this.dateString, this.getDateInputFormat()).startOf('day') : undefined;
    if (!date) { console.warn('Día inválido'); return; }
    const selectedWeekday = date.isoWeekday();

    const { from, to } = this.getRepeatDateRange();

    const today = from.startOf('day');
    const horizon = to.startOf('day');
    const newDates: moment.Moment[] = [];

    let cursor = today.clone().startOf('day');
    while (cursor.isSameOrBefore(horizon)) {
      const monthDays: moment.Moment[] = [];
      const mStart = cursor.clone();
      const mEnd = cursor.clone().endOf('month');

      let c2 = mStart.clone();
      while (c2.isSameOrBefore(mEnd)) {
        if (c2.isoWeekday() === selectedWeekday) {
          monthDays.push(c2.clone());
        }
        c2.add(1, 'day');
      }

      let pick: moment.Moment | null = null;
      const ord = this.monthlyOrdinalSelected!;
      if (ord > 0 && ord <= monthDays.length) {
        pick = monthDays[ord - 1];
      } else if (ord === 0 && monthDays.length) {
        pick = monthDays[monthDays.length - 1];
      }

      if (pick) {
        const key = pick.format('YYYY-MM-DD');
        if (!existingKeys.has(key)) {
          newDates.push(pick);
          existingKeys.add(key);
        }
      }

      cursor.add(1, 'month').startOf('month');
    }

    this.reminder.executionDates = [
      ...existing,
      ...newDates
    ].sort((a, b) => a.valueOf() - b.valueOf());

    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));

    this.validateDateTime();
  }

  clearDates() {
    this.rangeEnd = 0;
    this.rangeStart = 0;
    this.fromDateSelected = "";
    this.toDateSelected = "";
    this.weeklyDayInput = "";
    this.monthlyWeekdayInput = "";
    this.monthlyOrdinalInput = "";
    this.reminder.executionDates = [];
    this.customDateClasses = [];
    this.validateDateTime();
  }

  onStartSelect(event: TypeaheadMatch) {
    this.rangeStart = event.item.value;
    this.manageGenerateDates();
  }

  onEndSelect(event: TypeaheadMatch) {
    this.rangeEnd = event.item.value;
    this.manageGenerateDates();
  }

  onMonthlyOrdinalSelect(event: any) {
    const ord = event.item as { label: string; value: number };
    this.monthlyOrdinalInput = ord.label;
    this.monthlyOrdinalSelected = ord.value;
    this.manageGenerateDates();
  }

  getWeekdayName(val: number): string {
    const w = this.weekdays.find(d => d.value === val);
    return w ? w.name : '';
  }

  managePause() {
    this._reminderService.updateReminderPause(this.reminder.id).subscribe(result => {
      this.reminder.paused = !this.reminder.paused;
      this.notify.success(this.l('EditedSuccessfully'));
    });
  }

  getFirstExecutionDate(): moment.Moment | null {
    if (!this.reminder.executionDates || this.reminder.executionDates.length === 0) {
      // If no execution dates, try to get from dateString
      if (this.dateString) {
        return moment(this.dateString, this.getDateInputFormat());
      }
      // For same day execution, if no dateString is set, use today
      if (this.selectedNotifyBefore === this.l('SameDay') || this.selectedNotifyBefore === '') {
        return moment().startOf('day');
      }
      return null;
    }

    // Get the first (earliest) execution date
    const sortedDates = this.reminder.executionDates.sort((a, b) => a.valueOf() - b.valueOf());
    return sortedDates[0];
  }

  getActualLastExecutionDate(): string {
    // For non-repeating reminders, the end date should be the same as the start date
    if (!this.repeat) {
      const firstExecutionDate = this.getFirstExecutionDate();
      if (firstExecutionDate) {
        return DateHelper.formatDate(firstExecutionDate, this.getDateInputFormat());
      }
      // Fallback to dateString if available
      if (this.dateString) {
        return this.dateString;
      }
      return this.l('Sin fecha fin');
    }

    // For repeating reminders
    if (!this.reminder.executionDates || this.reminder.executionDates.length === 0) {
      return this.endDateString ? this.endDateString : this.l('Sin fecha fin');
    }

    // Get the last execution date from the generated dates
    const lastExecutionDate = this.reminder.executionDates
      .sort((a, b) => b.valueOf() - a.valueOf())[0]; // Sort descending and get the last one
    return DateHelper.formatDate(lastExecutionDate, this.getDateInputFormat());
  }

  get nextExecutionDate(): moment.Moment | null {
    if (!this.reminder.executionDates?.length) {
      return null;
    }

    const now = moment();
    const daysBefore = this.reminder.daysBefore ?? 0;

    const nextAdjusted = this.reminder.executionDates
      .map(orig => orig.clone().subtract(daysBefore, 'days'))
      .find(adjusted => adjusted.isAfter(now, 'second'));

    return nextAdjusted || null;
  }

  public get summaryText(): string {
    const typeName = this.getTypeName(this.reminder.reminderTypeId) || this.l('Sin tipo');

    const firstExecutionDate = this.getFirstExecutionDate();
    const startDate = firstExecutionDate
      ? DateHelper.formatDate(firstExecutionDate, this.getDateInputFormat())
      : this.l('Sin fecha inicio');
    const startTime = this.timeString || this.l('Sin hora');

    const toNames = this.assignedRecipientsIds.length
      ? this.assignedRecipientsIds.map(u => this.recipientOptions.find(f => f.id == u)?.fullName).join(', ')
      : this.l('Sin destinatarios');
    const priorityName = this.getPriorityName(this.reminder.reminderPriorityId);
    const prioText = priorityName ? `${this.l('Prioridad')}: ${priorityName}` : '';

    const nextExec = this.nextExecutionDate
      ? DateHelper.formatDate(this.nextExecutionDate, this.getDateInputFormat())
      : this.l('Sin próxima ejecución');

    const endDate = this.getActualLastExecutionDate();

    return [
      this.l('Recordatorio') + ` “${typeName}”`,
      `${this.l('Comienza el')} ${startDate} ${this.l('a las')} ${startTime}`,
      `${this.l('Para')}: ${toNames}`,
      prioText,
      `${this.l('próxima ejecución')}: ${nextExec}`,
      endDate != this.l('Sin fecha fin') ? `${this.l('Hasta el')} ${endDate}` : endDate
    ]
      .filter(seg => !!seg)
      .join('. ') + '.';
  }

  onEndDateChange(selected: Date) {
    if (selected != undefined) {
      this.manageGenerateDates();
    }
  }

  manageGenerateDates() {
    if (this.repeat) {
      switch (this.repeatModeSelected) {
        case 'Daily':
          this.generateDates();
          break;
        case 'Weekly':
          this.generateWeeklyDates();
          break;
        case 'Monthly':
          this.generateMonthlyDates();
        default:
          break;
      }
    }
  }

  validateDateTime(): void {
    this.haveChangesToSave = true;
    this.isDateTimeValid = true;

    if (!this.repeat) {
      this.reminder.executionDates = [moment(this.dateString, this.getDateInputFormat()).utc(false)];
    }

    this.customDateClasses = this.reminder.executionDates.map(d => ({
      date: d.toDate(),
      classes: ['selected-date']
    }));

    if (this.reminder.executionDates.length === 0 || !this.selectedTimezone) {
      return;
    }

    const tzOffsetMin = this.parseTimezoneOffset(this.selectedTimezone);
    const nowLocal = new Date();
    const nowUtc = new Date(nowLocal.getTime() + nowLocal.getTimezoneOffset() * 60 * 1000);
    const nowInTz = new Date(nowUtc.getTime() + tzOffsetMin * 60 * 1000);

    const selDate: Date = this.reminder.executionDates
      .sort((a, b) => a.diff(b))[0]
      .toDate();

    const y = selDate.getFullYear();
    const m = selDate.getMonth();
    const d = selDate.getDate();

    const [hh, mm2] = this.timeString.split(':').map(s => parseInt(s, 10));

    const selectedInTz = new Date(nowInTz);
    selectedInTz.setFullYear(y, m, d);
    selectedInTz.setHours(hh, mm2, 0, 0);

    let daysBefore = 0;
    if (this.selectedNotifyBefore == this.l("1Day") || this.selectedNotifyBefore == this.l("2Days")) {
      if (this.selectedNotifyBefore === this.l('1Day')) daysBefore = 1;
      else if (this.selectedNotifyBefore === this.l('2Days')) daysBefore = 2;
    }

    const checkTime = new Date(
      selectedInTz.getTime() - daysBefore * 24 * 60 * 60 * 1000
    );

    if (checkTime.getTime() <= nowInTz.getTime()) {
      this.isDateTimeValid = false;
    }
  }

  onDateBlur(): void {
    this.validateDateTime();
    setTimeout(() => {
      this.timeInput.nativeElement.focus();
    });
  }

  private parseTimezoneOffset(tz: string): number {
    const match = tz.match(/^UTC([+-])(\d{1,2}):(\d{2})$/);
    if (!match) {
      console.error(`Formato de zona horaria inválido: ${tz}`);
      return 0;
    }
    const [, sign, hh, mm] = match;
    const offsetMin = parseInt(hh, 10) * 60 + parseInt(mm, 10);
    return sign === '+' ? offsetMin : -offsetMin;
  }

  manageRepeat() {
    this.repeat = !this.repeat;

    if (this.repeat) {
      this.repeatModeSelected = 'Daily';
      this.fromDateSelected = this.l('Monday');
      this.toDateSelected = this.l('Friday');
      this.rangeStart = 1;
      this.rangeEnd = 5;
      this.selectedNotifyBefore = '';
      this.generateDates();
    }

    if (!this.repeat) {
      this.reminder.executionDates = [moment(this.dateString, this.getDateInputFormat()).utc(false)];
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.showTypeDropdown) {
      const typeInput = document.getElementById('reminderType' + this.randomTypeId);
      if (typeInput && !typeInput.contains(target) && !this.isInsideDropdown(target, 'type')) {
        this.showTypeDropdown = false;
      }
    }

    if (this.showPriorityDropdown) {
      const priorityInput = document.getElementById('reminderPriority' + this.randomPriorityId);
      if (priorityInput && !priorityInput.contains(target) && !this.isInsideDropdown(target, 'priority')) {
        this.showPriorityDropdown = false;
      }
    }
  }

  handleOnClose() {
    if (this.haveChangesToSave) {
      abp.message.confirm(
        this.l("SureHaveChangesToSave"),
        undefined,
        (result: boolean) => {
          if (result) {
            if (!this.isDateTimeValid || !this.createOrEditReminderForm.form.valid || this.saving || this.assignedRecipientsIds.length == 0 || this.reminder.reminderTypeId == undefined) {
              abp.message.error(this.l("MandatoryFieldsNotCompleted"));
            } else {
              this.save();
            }
          } else {
            this.onCloseModal();
          }
        }
      );
    } else {
      this.onCloseModal();
    }
  }

  private isInsideDropdown(target: HTMLElement, type: 'type' | 'priority'): boolean {
    if (type === 'type') {
      const dropdown = document.querySelector('.dropdown-menu.show');
      return dropdown ? dropdown.contains(target) : false;
    }
    if (type === 'priority') {
      const dropdown = document.querySelector('.dropdown-menu.show');
      return dropdown ? dropdown.contains(target) : false;
    }
    return false;
  }

  getRepeatDateRange(): { from: moment.Moment; to: moment.Moment } {
    const from = this.dateString != '' ? moment(this.dateString, this.getDateInputFormat()).startOf('day') : moment().startOf('day');
    const to = this.endDateString
      ? moment(this.endDateString, this.getDateInputFormat()).startOf('day')
      : from.clone().add(10, 'years');

    return { from, to };
  }

  handleEventData(): void {
    if (!this.eventData) return;

    if (this.eventData.date) {
      this.dateString = DateHelper.formatDate(this.eventData.date, this.getDateInputFormat());
      const timeFormat = this.eventData.date.format('HH:mm');
      if (timeFormat !== '00:00') {
        this.timeString = timeFormat;
      }
    }

    if (this.eventData.companyId) {
      this.reminder.companyId = this.eventData.companyId;
      this._companyService.get(this.eventData.companyId).subscribe(company => {
        this.assignedFieldSearchField = company.name;
      });
    } else if (this.eventData.juridicalPersonId) {
      this.reminder.juridicalPersonId = this.eventData.juridicalPersonId;
      this._juridicalPersonService.get(this.eventData.juridicalPersonId).subscribe(juridicalPerson => {
        this.assignedFieldSearchField = juridicalPerson.name;
      });
    } else if (this.eventData.naturalPersonId) {
      this.reminder.naturalPersonId = this.eventData.naturalPersonId;
      this._naturalPersonService.get(this.eventData.naturalPersonId).subscribe(naturalPerson => {
        this.assignedFieldSearchField = naturalPerson.names + " " + naturalPerson.surnames;
      });
    }

    if (this.appSession.userId) {
      this.assignedRecipientsIds = [this.appSession.userId];
    }

    this.haveChangesToSave = true;
  }

  handlePrefilledData(): void {
    if (!this.prefilledData) return;

    this._reminderService.handleTaskReminderType().subscribe(taskReminderTypeId => {
      this._reminderService.getAllReminderTypes().subscribe(result => {
        this.reminderTypes = result.map(t => {
          (t as any).uid = (t as any).uid || this.generateUid();
          return t;
        });

        this.reminder.reminderTypeId = taskReminderTypeId;

        this.cd.detectChanges();

        if (this.prefilledData.description) {
          this.reminder.description = this.prefilledData.description;
        }

        if (this.prefilledData.assignedRecipients && this.prefilledData.assignedRecipients.length > 0) {
          this.assignedRecipientsIds = [...this.prefilledData.assignedRecipients];
        }

        if (this.prefilledData.reminderDate) {
          this.dateString = DateHelper.formatDate(this.prefilledData.reminderDate, this.getDateInputFormat());
          const timeFormat = this.prefilledData.reminderDate.format('HH:mm');
          if (timeFormat !== '00:00') {
            this.timeString = timeFormat;
          }
        }

        this.haveChangesToSave = true;
      });
    });
  }
}
